import { property } from '@wonderlandengine/api/decorators.js';
import { Component, Material, MeshComponent, Object3D } from '@wonderlandengine/api';
import { Configuration } from '../data/configuration.js';
import { Utilities } from '../utilities/utilities.js';

/**
 * Sub-Model Configurator:
 */
export class SubModelConfigurator extends Component {
	static TypeName = 'sub-model-configurator';

	//-----------------------------------------------------------------------------------------
	// Inspector Variables:
	//-----------------------------------------------------------------------------------------

	@property.object({ required: true })
	frontWing!: Object3D;
	
	@property.object({ required: true })
	frontGrills!: Object3D;

	@property.object({ required: true })
	rearGrill!: Object3D;

	@property.object({ required: true })
	dials!: Object3D;

	@property.object({ required: true })
	doorLeather!: Object3D;

	@property.object({ required: true })
	superliteWheels!: Object3D;

	@property.object({ required: true })
	frontSplitter!: Object3D;

	@property.object({ required: true })
	rearDiffuser!: Object3D;

	@property.object({ required: true })
	sideSkirts!: Object3D;

	@property.object({ required: true })
	bonnetVents!: Object3D;

	@property.object({ required: true })
	licensePlateFront!: Object3D;

	@property.object({ required: true })
	licensePlateRear!: Object3D;

	@property.object({ required: true })
	supersport400!: Object3D;

	@property.object({ required: true })
	fuelCapUsa!: Object3D;

	@property.object({ required: true })
	licensePlateUsFront!: Object3D;

	@property.object({ required: true })
	licensePlateUsBack!: Object3D;

	@property.material({ required: true })
	blackPlasticShinyMaterial!: Material;
	
	@property.material({ required: true })
	licensePlateMaterial400!: Material;

	//-----------------------------------------------------------------------------------------
	// Fields:
	//-----------------------------------------------------------------------------------------

	// component references.
	private _frontSplitter!: MeshComponent;
	private _rearDiffuser!: MeshComponent;
	private _sideSkirts!: MeshComponent;
	private _bonnetVents!: MeshComponent;
	private _licensePlateFront!: MeshComponent;
	private _licensePlateRear!: MeshComponent;
	
	//-----------------------------------------------------------------------------------------
	// Lifecycle Functions:
	//-----------------------------------------------------------------------------------------

	start() {
		this._frontSplitter = this.frontSplitter.getComponent(MeshComponent)!;
		this._rearDiffuser = this.rearDiffuser.getComponent(MeshComponent)!;
		this._sideSkirts = this.sideSkirts.getComponent(MeshComponent)!;
		this._bonnetVents = this.bonnetVents.getComponent(MeshComponent)!;
		this._licensePlateFront = this.licensePlateFront.getComponent(MeshComponent)!;
		this._licensePlateRear = this.licensePlateRear.getComponent(MeshComponent)!;
	}

	//-----------------------------------------------------------------------------------------
	// Public Functions:
	//-----------------------------------------------------------------------------------------

	/*
	 *  Selects the appropriate sub-model based on the current configuration.
	 */
	selectSubModel() {

		// configure for Supersport 400 model.
		if (Configuration.isSupersport400Model()) {

			// enable all the meshes under the 'supersport400' node, as these are the new parts for this model.
			// n.b. some of these meshes may be subsequently disabled depending on the default configuration.
			Utilities.setActiveAll(this.supersport400, true);

			// disable the relevant nodes from the main model, as these are replaced by the new meshes in the 'supersport400' node.
			this.frontWing.active = false;
			this.frontGrills.active = false;
			this.rearGrill.active = false;
			this.dials.active = false;
			this.doorLeather.active = false;
			Utilities.setActiveAll(this.superliteWheels, false);

			// set the materials for the front splitter, rear diffuser and side skirts to the black plastic shiny material (instead of black plastic).
			this._frontSplitter.material = this.blackPlasticShinyMaterial;
			this._rearDiffuser.material = this.blackPlasticShinyMaterial;
			this._sideSkirts.material = this.blackPlasticShinyMaterial;
			this._bonnetVents.material = this.blackPlasticShinyMaterial;

			// set the licence plate materials to the custom 'licensePlateMaterial400' material, which has the correct licence plate texture for this model.
			this._licensePlateFront.material = this.licensePlateMaterial400;
			this._licensePlateRear.material = this.licensePlateMaterial400;

			// add the silver exhaust option to the accessories configurator, as this comes as standard with this model.
			Configuration.addAccessoryOption(Configuration.accessoryEquipmentOptions, Configuration.silverSportsExhaustOption);
		}

		// configure for standard model.
		else {

			// disable all the meshes under the 'supersport400' node.
			Utilities.setActiveAll(this.supersport400, false);

			// enable the Supersport US options if applicable.
			if (Configuration.isSupersportModelInUsRegion()) {

				// n.b. the US cap is bigger than the standard one, so no need to disable the standard one.
				this.fuelCapUsa.active = true;

				// enable the US licence plates and disable the standard ones.
				this.licensePlateUsFront.active = true;
				this.licensePlateUsBack.active = true;
				this.licensePlateFront.active = false;
				this.licensePlateRear.active = false;
			}
			else {

				// disable the US options.
				this.fuelCapUsa.active = false;
				this.licensePlateUsFront.active = false;
				this.licensePlateUsBack.active = false;

				// enable the standard options.
				this.licensePlateFront.active = true;
				this.licensePlateRear.active = true;
			}
		}
	}
}
